import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Clock3, Share2, Workflow, LineChart, ShieldCheck, BarChart3, Zap, Eye, GitBranch, Briefcase, Building2, TrendingUp, Home, Network, Rocket, Target, Layers, Compass } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import DemoDialog from "@/components/site/DemoDialog";
import AmbientParticles from "@/components/site/AmbientParticles";
import StandardCTA from "@/components/site/StandardCTA";
import { SOLUTIONS_IMAGES } from "@/lib/site";

function Eyebrow({ children }) {
  return <div className="text-[10.5px] tracking-[0.38em] text-sapphire-glow uppercase font-medium">{children}</div>;
}

const CARDS = [
  { slug: "brokerages",     iconMark: Briefcase,  img: SOLUTIONS_IMAGES.cardBrokerages,    title: "Brokerages",     desc: "Capture, qualify, route, and convert more leads with intelligent automation and CRM intelligence." },
  { slug: "developers",     iconMark: Building2,  img: SOLUTIONS_IMAGES.cardDevelopers,    title: "Developers",     desc: "Unify sales, inventory, marketing, and reporting across projects and communities." },
  { slug: "investors",      iconMark: TrendingUp, img: SOLUTIONS_IMAGES.cardInvestors,     title: "Investors",      desc: "Gain portfolio visibility, track performance, and make data-driven investment decisions." },
  { slug: "property-teams", iconMark: Home,       img: SOLUTIONS_IMAGES.cardPropertyTeams, title: "Property Teams", desc: "Streamline operations, engage tenants, and improve efficiency across your properties." },
  { slug: "enterprises",    iconMark: Network,    img: SOLUTIONS_IMAGES.cardEnterprises,   title: "Enterprises",    desc: "Manage complex portfolios with enterprise-grade intelligence, governance, and automation." },
];

const LAYER = [
  { icon: Clock3,     title: "Real-Time Intelligence", desc: "Live data and insights across the entire real estate lifecycle." },
  { icon: Share2,     title: "Data Unification",       desc: "Connect all your data sources into one intelligent platform." },
  { icon: Workflow,   title: "Workflow Automation",    desc: "Automate repetitive tasks and accelerate your teams." },
  { icon: LineChart,  title: "Predictive Analytics",   desc: "Anticipate trends and make confident, data-driven decisions." },
  { icon: ShieldCheck,title: "Enterprise Security",    desc: "Bank-grade security, privacy, and role-based access control." },
  { icon: BarChart3,  title: "Executive Visibility",   desc: "Real-time dashboards and reporting for executive teams." },
];

const OUTCOMES = [
  { icon: Clock3,     title: "Faster Response Times", desc: "Engage leads instantly and reduce response time across all channels." },
  { icon: Eye,        title: "Improved Visibility",   desc: "Gain complete visibility across leads, teams, and operations." },
  { icon: LineChart,  title: "Better Decision Making",desc: "Use real-time data and analytics to make smarter decisions." },
  { icon: Zap,        title: "Increased Efficiency",  desc: "Automate workflows and eliminate manual tasks." },
  { icon: ShieldCheck,title: "Stronger Governance",   desc: "Maintain control with security, compliance, and auditability." },
  { icon: GitBranch,  title: "Scalable Operations",   desc: "Scale your business with a platform built for the future." },
];

const ENGAGEMENT = [
  {
    key: "pilot",
    icon: Rocket,
    name: "Pilot",
    tagline: "One fix, fast.",
    desc: "A single high-impact automation, live within days. Built to solve your most urgent leak — usually response time — so you see the result before committing to anything larger.",
    included: [
      "Instant response and capture across your primary enquiry channel",
      "Every enquiry acknowledged and routed to the right person automatically",
      "No retainer, no long-term commitment",
    ],
  },
  {
    key: "growth",
    icon: Target,
    name: "Growth",
    tagline: "The core system most operators land on.",
    desc: "Lead capture unified across every source you use, structured qualification against your own criteria, and a follow-up cadence that holds the same standard at scale — so nothing slips through.",
    included: [
      "Multi-source lead capture, one system of record",
      "Structured qualification before a human spends time",
      "A defined multi-touch follow-up cadence",
      "Rule-based routing by segment, territory, or value",
    ],
  },
  {
    key: "infrastructure",
    icon: Layers,
    name: "Infrastructure",
    tagline: "Built for scale. Built for global operations.",
    desc: "Multi-channel, multilingual handling and deeper system integration, for developers and enterprise operations working across markets.",
    included: [
      "Multi-channel, multilingual coverage (English / Arabic / Hindi)",
      "Multi-market configuration (Dubai/GCC-ready)",
      "Deeper CRM integration",
      "Priority support",
    ],
  },
  {
    key: "elite",
    icon: Compass,
    name: "Elite",
    tagline: "Built around your operation, not a template.",
    desc: "A dedicated build and account team for developers and enterprise groups running multiple projects or markets at once — custom integrations, priority response, and direct access to the people running your system, not a support queue.",
    included: [
      "Dedicated build and account team",
      "Custom integrations for your operation",
      "Priority response, direct access",
      "Multi-project, multi-market coverage",
    ],
  },
];

export default function SolutionsPage() {
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setScrollY(window.scrollY));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  // Parallax — hero hub drifts up against scroll, CTA crystal drifts down with scroll
  const heroParallax  = `translate3d(0, ${-scrollY * 0.18}px, 0)`;
  const ctaParallax   = `translate3d(0, ${ scrollY * 0.08}px, 0)`;

  return (
    <div className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden" data-testid="solutions-page">
      {/* Page-level ambient dust-mote field — Palantir-grade atmospheric depth */}
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden>
        <AmbientParticles count={36} tone="mixed" density={0.6} />
      </div>
      <Navbar onBookDemo={() => setDemoOpen(true)} />

      {/* HERO */}
      <section className="relative pt-[120px] lg:pt-[140px] pb-14 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none bg-grid opacity-[0.08]" aria-hidden />
        <div
          className="absolute -top-32 right-0 w-[820px] h-[820px] pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(96,165,250,0.18), transparent)" }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_1.25fr] gap-8 lg:gap-12 items-center min-h-[560px] lg:min-h-[660px]">
            <div className="fade-up relative z-10">
              <Eyebrow>SOLUTIONS</Eyebrow>
              <h1 className="font-display mt-4 text-platinum text-[44px] sm:text-[56px] lg:text-[64px] xl:text-[68px] leading-[1.04] tracking-[-0.022em]">
                Solutions Built for<br />
                <span className="text-sapphire-glow" style={{ textShadow: "0 0 40px rgba(96,165,250,0.32)" }}>Every Real Estate</span><br />
                <span className="text-sapphire-glow" style={{ textShadow: "0 0 40px rgba(96,165,250,0.32)" }}>Organization</span>
              </h1>
              <p className="mt-7 text-[15px] sm:text-[16px] text-platinum/70 leading-[1.75] max-w-[540px]">
                From brokerages and developers to investors and enterprise portfolios,
                Esthera RealityOS delivers intelligence, automation, and operational
                visibility across the entire real estate lifecycle.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <a
                  href="#solutions-cards"
                  data-testid="hero-explore-solutions"
                  className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2"
                >
                  Explore Solutions <ArrowRight className="w-4 h-4" />
                </a>
                <button
                  onClick={() => setDemoOpen(true)}
                  data-testid="hero-book-demo"
                  className="btn-ghost rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2"
                >
                  Book a Demo <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* RIGHT — orbital hub, FULL-BLEED homepage style.
                 Image bleeds beyond container, fades into page via radial mask + edge gradients. */}
            <div className="relative lg:-mr-10 xl:-mr-20" data-testid="hero-visual"
              style={{ transform: heroParallax, willChange: "transform" }}>
              {/* Atmospheric sapphire depth */}
              <div className="absolute inset-0 pointer-events-none"
                style={{ background: "radial-gradient(60% 55% at 55% 50%, rgba(37,99,235,0.28), transparent 72%)", filter: "blur(50px)" }} aria-hidden />

              {/* Animated orbital rings (subtle, behind image) */}
              <div className="absolute inset-[10%] rounded-full pointer-events-none slow-spin"
                style={{ border: "1px dashed rgba(96,165,250,0.18)" }} aria-hidden />
              <div className="absolute inset-[20%] rounded-full pointer-events-none slow-spin-reverse"
                style={{ border: "1px dashed rgba(147,197,253,0.13)" }} aria-hidden />

              {/* Twinkling sparkles drifting around the hub */}
              <div className="absolute top-[14%] right-[16%] w-1.5 h-1.5 rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "0.4s", boxShadow: "0 0 12px rgba(96,165,250,1)" }} aria-hidden />
              <div className="absolute top-[42%] left-[8%] w-1 h-1 rounded-full bg-amber-200 twinkle" style={{ animationDelay: "1.2s", boxShadow: "0 0 10px rgba(252,211,77,0.95)" }} aria-hidden />
              <div className="absolute bottom-[26%] right-[22%] w-1 h-1 rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "2.0s", boxShadow: "0 0 10px rgba(96,165,250,1)" }} aria-hidden />
              <div className="absolute bottom-[34%] left-[18%] w-[5px] h-[5px] rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "0.9s", boxShadow: "0 0 14px rgba(96,165,250,1)" }} aria-hidden />

              <div className="relative">
                <img src={SOLUTIONS_IMAGES.heroHub} alt="Esthera RealityOS Intelligence Hub — orbital network connecting brokerages, developers, investors, enterprises and property teams"
                  className="block w-full h-[520px] lg:h-[680px] object-contain object-center breathe-glow slow-float"
                  style={{
                    mixBlendMode: "screen",
                    filter: "brightness(0.82) contrast(3.2) saturate(1.30)",
                    WebkitMaskImage: "radial-gradient(78% 88% at 50% 50%, #000 54%, rgba(0,0,0,0.45) 78%, transparent 100%)",
                    maskImage: "radial-gradient(78% 88% at 50% 50%, #000 54%, rgba(0,0,0,0.45) 78%, transparent 100%)",
                  }}
                  draggable="false" />
                {/* Vertical edge fade — dark top + very dark bottom */}
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.75) 0%, rgba(10,15,26,0) 18%, rgba(10,15,26,0) 70%, rgba(10,15,26,0.98) 100%)" }} aria-hidden />
                {/* Horizontal edge fade — heavy left fade into copy column */}
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(90deg, rgba(10,15,26,0.85) 0%, rgba(10,15,26,0.20) 16%, rgba(10,15,26,0) 35%, rgba(10,15,26,0) 78%, rgba(10,15,26,0.55) 100%)" }} aria-hidden />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTIONS CARDS */}
      <section id="solutions-cards" className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <h2 className="font-display text-center text-platinum text-[28px] sm:text-[34px] lg:text-[40px] leading-tight tracking-[-0.015em] fade-up">
            Solutions for Every Real Estate Business
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
            {CARDS.map((c, i) => (
              <Link
                key={c.slug}
                to={`/solutions/${c.slug}`}
                data-testid={`solution-card-${c.slug}`}
                className="group relative rounded-2xl overflow-hidden border border-white/[0.08] hover:border-sapphire-glow/45 transition-all duration-500 fade-up min-h-[440px] flex flex-col"
                style={{
                  background: "linear-gradient(180deg, rgba(15,22,40,0.5) 0%, rgba(10,15,26,0.85) 100%)",
                  animationDelay: `${i * 90}ms`,
                  boxShadow: "0 0 0 rgba(96,165,250,0)",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 36px -8px rgba(96,165,250,0.45)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 0 0 rgba(96,165,250,0)"; }}
              >
                {/* Full-card building image (heavily tinted to blend with the dark page) */}
                <div className="absolute inset-0 overflow-hidden">
                  <img src={c.img} alt={c.title}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.06]"
                    style={{
                      filter: "brightness(0.55) saturate(1.25) hue-rotate(-8deg)",
                    }}
                    draggable="false" />
                  {/* Sapphire color-grade wash so the image reads as part of the brand */}
                  <div className="absolute inset-0"
                    style={{ background: "linear-gradient(180deg, rgba(13,30,62,0.45) 0%, rgba(10,15,26,0.30) 40%, rgba(10,15,26,0.92) 100%)", mixBlendMode: "multiply" }} />
                  {/* Top brand-glow scrim */}
                  <div className="absolute inset-x-0 top-0 h-[40%]"
                    style={{ background: "linear-gradient(180deg, rgba(37,99,235,0.18) 0%, transparent 100%)" }} />
                  {/* Bottom dark fade for text legibility */}
                  <div className="absolute inset-x-0 bottom-0 h-[65%]"
                    style={{ background: "linear-gradient(180deg, transparent 0%, rgba(10,15,26,0.75) 55%, rgba(10,15,26,0.96) 100%)" }} />
                </div>

                {/* Top sapphire hairline */}
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/55 to-transparent" />

                {/* Content layered on top of the image */}
                <div className="relative z-10 p-6 sm:p-7 flex-1 flex flex-col">
                  <div className="flex items-center gap-2.5 mb-auto">
                    <div className="w-9 h-9 rounded-md inline-flex items-center justify-center shrink-0"
                      style={{
                        background: "rgba(15,22,40,0.55)",
                        border: "1px solid rgba(96,165,250,0.5)",
                        boxShadow: "0 0 14px -2px rgba(96,165,250,0.55), inset 0 0 8px rgba(96,165,250,0.10)",
                      }}>
                      <c.iconMark className="w-4 h-4 text-sapphire-glow" />
                    </div>
                    <h3 className="font-display text-platinum text-[20px] sm:text-[22px] leading-tight tracking-tight">{c.title}</h3>
                  </div>
                  <div className="mt-32 sm:mt-40">
                    <p className="text-[13px] text-platinum/75 leading-[1.6]">{c.desc}</p>
                    <div className="mt-5 inline-flex items-center gap-1.5 text-sapphire-glow text-[12.5px] font-medium tracking-wide group-hover:gap-3 transition-all">
                      Learn more <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE WORK — engagement tiers (no prices, no highlighted "recommended") */}
      <section className="relative pb-16 lg:pb-20" data-testid="how-we-work">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="text-center max-w-[820px] mx-auto fade-up">
            <div className="eyebrow-positioning">HOW WE WORK</div>
            <h2 className="font-display mt-5 text-platinum text-[30px] sm:text-[38px] lg:text-[44px] leading-tight tracking-[-0.018em]">
              How We Work
            </h2>
            <p className="mt-5 text-[15px] sm:text-[16px] text-platinum/72 leading-[1.75]">
              Every engagement starts with your Operations Audit. From there, we build to the scope
              that fits how your business actually runs.
            </p>
            <p className="mt-3 text-[13.5px] text-platinum/55 leading-[1.7] italic">
              We work with a small number of operators — each engagement scoped to your operation,
              for those ready to treat it as seriously as their assets.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {ENGAGEMENT.map((t, i) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.key}
                  data-testid={`engagement-${t.key}`}
                  className="group relative rounded-2xl border border-white/[0.08] overflow-hidden fade-up flex flex-col p-7 lg:p-8 transition-all duration-500 hover:border-sapphire-glow/45"
                  style={{
                    background: "linear-gradient(180deg, rgba(15,22,40,0.6) 0%, rgba(10,15,26,0.94) 100%)",
                    animationDelay: `${i * 90}ms`,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 34px -8px rgba(96,165,250,0.45)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
                >
                  <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/45 to-transparent" />

                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 rounded-full inline-flex items-center justify-center"
                      style={{
                        background: "radial-gradient(circle at 30% 30%, rgba(96,165,250,0.16), #0E1422 75%)",
                        border: "1px solid rgba(96,165,250,0.55)",
                        boxShadow: "0 0 14px -3px rgba(96,165,250,0.45)",
                      }}>
                      <Icon className="w-[18px] h-[18px] text-sapphire-glow" />
                    </div>
                    <div className="eyebrow-intelligence">{t.name.toUpperCase()}</div>
                  </div>

                  <h3 className="font-display text-platinum text-[22px] sm:text-[24px] leading-[1.15] tracking-[-0.012em]">
                    {t.tagline}
                  </h3>
                  <p className="mt-4 text-[13.5px] text-platinum/70 leading-[1.7]">
                    {t.desc}
                  </p>

                  <div className="mt-6 pt-5 border-t border-white/[0.06]">
                    <div className="text-[10.5px] tracking-[0.28em] uppercase font-medium text-platinum/50 mb-3.5">
                      Included
                    </div>
                    <ul className="space-y-2.5">
                      {t.included.map((line) => (
                        <li key={line} className="flex items-start gap-2.5 text-[12.5px] text-platinum/72 leading-[1.55]">
                          <span className="w-1 h-1 rounded-full bg-sapphire-glow shrink-0 mt-[7px]" style={{ boxShadow: "0 0 6px rgba(96,165,250,0.75)" }} />
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    onClick={() => navigate("/get-your-audit-report")}
                    data-testid={`engagement-${t.key}-cta`}
                    className="btn-primary rounded-md h-11 mt-7 px-5 text-[13px] font-medium inline-flex items-center justify-center gap-2"
                  >
                    Get Your Audit Report <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INTELLIGENCE LAYER */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="relative rounded-2xl overflow-hidden border border-white/[0.08] p-8 sm:p-12 lg:p-16"
            style={{ background: "linear-gradient(180deg, rgba(13,18,32,0.65) 0%, rgba(10,15,26,0.9) 100%)" }}>
            <img src={SOLUTIONS_IMAGES.intelligenceWave} alt="" aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              style={{ mixBlendMode: "screen", opacity: 0.38 }} draggable="false" />
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: "radial-gradient(ellipse at center, transparent 30%, rgba(10,15,26,0.65) 100%)" }} />
            <div className="relative">
              <h2 className="font-display text-center text-platinum text-[28px] sm:text-[34px] lg:text-[40px] leading-tight tracking-[-0.015em] fade-up">
                One Intelligence Layer Across Every Solution
              </h2>
              <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 lg:gap-7">
                {LAYER.map((l, i) => (
                  <div key={l.title} className="text-center fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                    <div className="mx-auto w-11 h-11 rounded-full inline-flex items-center justify-center"
                      style={{
                        background: "radial-gradient(circle at 30% 30%, rgba(96,165,250,0.20), #0E1422 70%)",
                        border: "1px solid rgba(96,165,250,0.45)",
                        boxShadow: "0 0 18px -3px rgba(96,165,250,0.45)",
                      }}>
                      <l.icon className="w-4.5 h-4.5 text-sapphire-glow" />
                    </div>
                    <div className="mt-3.5 font-display text-platinum text-[14px] sm:text-[15px] leading-tight">{l.title}</div>
                    <p className="mt-2 text-[11.5px] sm:text-[12px] text-platinum/55 leading-[1.55]">{l.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <h2 className="font-display text-center text-platinum text-[28px] sm:text-[34px] lg:text-[40px] leading-tight tracking-[-0.015em] fade-up">
            Outcomes That Drive Real Growth
          </h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-5">
            {OUTCOMES.map((o, i) => (
              <div key={o.title} className="relative rounded-2xl border border-white/[0.08] p-5 sm:p-6 hover:border-sapphire-glow/35 transition-colors fade-up"
                style={{
                  background: "linear-gradient(180deg, rgba(20,28,48,0.4) 0%, rgba(13,18,32,0.7) 100%)",
                  animationDelay: `${i * 80}ms`,
                }}>
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/30 to-transparent" />
                <o.icon className="w-5 h-5 text-sapphire-glow" />
                <div className="mt-3.5 font-display text-platinum text-[14px] sm:text-[15px] leading-tight">{o.title}</div>
                <p className="mt-2 text-[11.5px] sm:text-[12px] text-platinum/55 leading-[1.55]">{o.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA — uses StandardCTA (full-bleed pattern, uniform site-wide) */}
      <StandardCTA
        testId="solutions-cta"
        eyebrow="GET STARTED"
        eyebrowTone="positioning"
        title="Ready to transform your real estate operations?"
        highlight="transform your real estate operations?"
        subtitle="See how Esthera RealityOS helps real estate organizations operate with greater intelligence, visibility, and efficiency."
        crystal={SOLUTIONS_IMAGES.ctaDiamond}
        primaryLabel="Get Your Audit Report"
        primaryHref="/get-your-audit-report"
        secondaryLabel="Book a Demo"
        secondaryHref="/book-demo"
      />

      <Footer />
      <DemoDialog open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
